import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import './UserDashboard.css';

const NotificationsPanel = ({ onNavigate }) => {
  const { user } = useUser();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showReplyForm, setShowReplyForm] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyMsg, setReplyMsg] = useState('');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    // Fetch real inbox messages from support replies
    let inboxNotifications = [];
    try {
      const accountId = user.accountId;
      if (accountId) {
        const token = localStorage.getItem('auth_token');
        const response = await fetch('/api/inbox/' + accountId, {
          headers: { 'Authorization': 'Bearer ' + (token || '') }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            inboxNotifications = data.messages.map(function(msg) {
              return {
                id: 'inbox-' + msg.id,
                type: 'support_response',
                category: 'inbox',
                title: 'Support Reply: ' + (msg.subject || 'New message'),
                message: msg.message ? msg.message.substring(0, 200) : 'You have a new reply from support.',
                fullMessage: msg.message || 'No message content available.',
                timestamp: msg.date || new Date().toISOString(),
                read: msg.status === 'read',
                actions: [
                  { label: 'Open Support Center', action: 'view_support' }
                ]
              };
            });
          }
        }
      }
    } catch (err) {
      console.log('Inbox fetch error:', err.message);
    }

    // Simulate fetching other notifications
    setTimeout(() => {
      const mockNotifications = [
        {
          id: 1,
          type: 'success',
          category: 'processing',
          title: 'Video Processing Complete',
          message: 'Your video "AI Tutorial 2024" has been successfully processed. 3 viral clips are ready for download.',
          timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
          read: true,
          actions: [
            { label: 'View Clips', action: 'view_clips' },
            { label: 'Download All', action: 'download_all' }
          ]
        },
        {
          id: 2,
          type: 'info',
          category: 'system',
          title: 'New AI Model Available',
          message: 'GPT-4 Turbo is now available for better clip generation. Your account has been automatically upgraded.',
          timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
          read: true,
          actions: [
            { label: 'Learn More', action: 'learn_more' }
          ]
        },
        {
          id: 3,
          type: 'warning',
          category: 'billing',
          title: 'Usage Limit Approaching',
          message: 'You have used 8/10 videos this month. Your limit resets in 3 days.',
          timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
          read: true,
          actions: [
            { label: 'Upgrade Plan', action: 'upgrade_plan' },
            { label: 'View Usage', action: 'view_usage' }
          ]
        },
        {
          id: 4,
          type: 'tip',
          category: 'optimization',
          title: 'Content Optimization Tip',
          message: 'Your TikTok videos perform 34% better when posted between 6-9 PM EST. Schedule your next upload accordingly.',
          timestamp: new Date(Date.now() - 1000 * 60 * 300).toISOString(),
          read: true,
          actions: [
            { label: 'View Analytics', action: 'view_analytics' }
          ]
        },
        {
          id: 5,
          type: 'achievement',
          category: 'milestone',
          title: 'Milestone Reached! 🎉',
          message: 'Congratulations! You\'ve created 100 viral clips. You\'re now eligible for our Creator Program.',
          timestamp: new Date(Date.now() - 1000 * 60 * 1440).toISOString(),
          read: true,
          actions: [
            { label: 'Join Program', action: 'join_program' },
            { label: 'Share Achievement', action: 'share_achievement' }
          ]
        },
        {
          id: 6,
          type: 'security',
          category: 'account',
          title: 'New Login Detected',
          message: 'Your account was accessed from a new device (MacBook Pro, Chrome) in New York, NY.',
          timestamp: new Date(Date.now() - 1000 * 60 * 480).toISOString(),
          read: true,
          actions: [
            { label: 'Secure Account', action: 'secure_account' },
            { label: 'View Sessions', action: 'view_sessions' }
          ]
        }
      ];
      setNotifications([...inboxNotifications, ...mockNotifications]);
      setIsLoading(false);
    }, 800);
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const handleAction = (action, notificationId) => {
    markAsRead(notificationId);
    if (!onNavigate) return;
    switch (action) {
      case 'view_clips': onNavigate('billing'); break;
      case 'download_all': onNavigate('billing'); break;
      case 'learn_more': onNavigate('documentation'); break;
      case 'upgrade_plan': onNavigate('billing'); break;
      case 'view_usage': onNavigate('billing'); break;
      case 'view_analytics': onNavigate('analytics'); break;
      case 'view_achievements': onNavigate('dashboard'); break;
      case 'view_support':
          setExpandedId(expandedId === notificationId ? null : notificationId);
          if (expandedId !== notificationId) {
            setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
            try {
              var aid = user && user.accountId;
              if (aid) {
                var tk = localStorage.getItem('auth_token');
                fetch('/api/inbox/' + aid + '/mark-read', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (tk || '') },
                  body: JSON.stringify({ messageIds: [notificationId] })
                }).then(() => {
                  if (window.__unreadInboxCount > 0) window.__unreadInboxCount--;
                  document.dispatchEvent(new CustomEvent('inbox-update'));
                });
              }
            } catch(e) {}
          }
          break;
      default: console.log('Action: ' + action);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'tip': return '💡';
      case 'achievement': return '🏆';
      case 'security': return '🔒';
      default: return '📢';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success': return { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)' };
      case 'info': return { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)' };
      case 'warning': return { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)' };
      case 'tip': return { bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.3)' };
      case 'achievement': return { bg: 'rgba(236, 72, 153, 0.1)', border: 'rgba(236, 72, 153, 0.3)' };
      case 'security': return { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)' };
      default: return { bg: 'rgba(255, 255, 255, 0.05)', border: 'rgba(255, 255, 255, 0.1)' };
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notif.read;
    return notif.category === filter;
  });

  const unreadCount = notifications.filter(notif => !notif.read).length;

  if (isLoading) {
    return (
      <div className="user-dashboard">
        <div style={{padding:'0 0 8px 0'}}><button className="dash-back-btn" onClick={() => { window.history.pushState({section:'dashboard'}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate', {state:{section:'dashboard'}})); }}>🏠 ← Dashboard</button></div>
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-dashboard">
      <div style={{padding:'0 0 8px 0'}}><button className="dash-back-btn" onClick={() => { window.history.pushState({section:'dashboard'}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate', {state:{section:'dashboard'}})); }}>🏠 ← Dashboard</button></div>
      <div className="dashboard-header">
        <div className="user-info">
          <h1>🔔 Inbox Notifications</h1>
          <p className="user-email">
            {unreadCount > 0 ? `${unreadCount} unread messages` : 'All caught up!'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button 
            onClick={async () => {
              const unreadIds = notifications.filter(n => !n.read && String(n.id).startsWith('inbox-')).map(n => n.id);
              setNotifications(prev => prev.map(n => ({ ...n, read: true })));
              window.__unreadInboxCount = 0;
              document.dispatchEvent(new CustomEvent('inbox-update'));
              try {
                const accountId = user && user.accountId;
                if (accountId && unreadIds.length > 0) {
                  const token = localStorage.getItem('auth_token');
                  await fetch('/api/inbox/' + accountId + '/mark-read', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (token || '') },
                    body: JSON.stringify({ messageIds: unreadIds })
                  });
                }
              } catch(e) { console.log('Mark read error:', e); }
            }}
            style={{
              padding: '8px 16px',
              background: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: '8px',
              color: '#6366f1',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Mark All Read
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {['all', 'unread', 'inbox', 'processing', 'system', 'billing', 'optimization'].map(filterType => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            style={{
              padding: '8px 16px',
              background: filter === filterType ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.05)',
              border: `1px solid ${filter === filterType ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
              borderRadius: '20px',
              color: filter === filterType ? '#ffffff' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              textTransform: 'capitalize',
              transition: 'all 0.2s ease'
            }}
          >
            {filterType} {filterType === 'unread' && unreadCount > 0 && `(${unreadCount})`}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative', zIndex: 1 }}>
        {filteredNotifications.map((notification) => {
          const colors = getNotificationColor(notification.type);
          return (
            <div
              key={notification.id}
              style={{
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: '16px',
                padding: '24px',
                position: 'relative',
                opacity: notification.read ? 0.7 : 1,
                transition: 'all 0.3s ease'
              }}
            >
              {!notification.read && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  width: '8px',
                  height: '8px',
                  background: '#6366f1',
                  borderRadius: '50%',
                  boxShadow: '0 0 10px rgba(99, 102, 241, 0.8)'
                }} />
              )}
              
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ fontSize: '24px', flexShrink: 0 }}>
                  {getNotificationIcon(notification.type)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <h3 style={{
                      color: '#ffffff',
                      fontWeight: '700',
                      fontSize: '18px',
                      margin: '0',
                      lineHeight: '1.2'
                    }}>
                      {notification.title}
                    </h3>
                    <span style={{
                      color: '#94a3b8',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {formatTimeAgo(notification.timestamp)}
                    </span>
                  </div>
                  
                  {expandedId === notification.id ? (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '16px',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                        paddingBottom: '12px',
                        borderBottom: '1px solid rgba(99, 102, 241, 0.15)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '16px' }}>📧</span>
                          <span style={{ color: '#a5b4fc', fontSize: '13px', fontWeight: '600' }}>
                            {notification.title && notification.title.includes('Re:') ? 'Admin Reply' : 'Support Ticket'}
                          </span>
                        </div>
                        <span style={{ color: '#64748b', fontSize: '12px' }}>
                          {formatTimeAgo(notification.timestamp)}
                        </span>
                      </div>
                      <div style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        paddingRight: '8px'
                      }}>
                        <p style={{
                          color: '#e2e8f0',
                          fontSize: '14px',
                          lineHeight: '1.8',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {notification.fullMessage || notification.message}
                        </p>
                      </div>
                      <div style={{
                        marginTop: '16px',
                        paddingTop: '12px',
                        borderTop: '1px solid rgba(99, 102, 241, 0.15)',
                        display: 'flex',
                        gap: '10px',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => setExpandedId(null)}
                          style={{
                            padding: '10px 20px',
                            background: 'rgba(99, 102, 241, 0.25)',
                            border: '1px solid rgba(99, 102, 241, 0.5)',
                            borderRadius: '8px',
                            color: '#a5b4fc',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          ← Back to Inbox
                        </button>
                        <button
                          onClick={() => {
                            setShowReplyForm(showReplyForm === notification.id ? null : notification.id);
                            setReplyText('');
                            setReplyMsg('');
                          }}
                          style={{
                            padding: '10px 20px',
                            background: showReplyForm === notification.id ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : 'linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.3))',
                            border: '1px solid rgba(99, 102, 241, 0.5)',
                            borderRadius: '8px',
                            color: showReplyForm === notification.id ? '#fff' : '#c4b5fd',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {showReplyForm === notification.id ? '✕ Cancel Reply' : '💬 Reply'}
                        </button>
                        <button
                          onClick={() => onNavigate('support')}
                          style={{
                            padding: '10px 20px',
                            background: 'rgba(34, 197, 94, 0.15)',
                            border: '1px solid rgba(34, 197, 94, 0.4)',
                            borderRadius: '8px',
                            color: '#86efac',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          💬 New Ticket
                        </button>
                      </div>
                      {showReplyForm === notification.id && (
                        <div style={{marginTop:'16px',padding:'16px',background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:'12px'}}>
                          <label style={{color:'#a5b4fc',fontSize:'13px',fontWeight:'600',display:'block',marginBottom:'8px'}}>Your Reply</label>
                          <textarea 
                            value={replyText} 
                            onChange={e=>setReplyText(e.target.value)} 
                            placeholder="Type your reply here..." 
                            rows={4} 
                            style={{width:'100%',padding:'12px',borderRadius:'8px',border:'1px solid rgba(255,255,255,0.15)',background:'rgba(255,255,255,0.05)',color:'#e2e8f0',fontSize:'14px',marginBottom:'12px',boxSizing:'border-box',resize:'vertical',fontFamily:'inherit'}} 
                          />
                          <button
                            onClick={async()=>{
                              if(!replyText.trim())return;
                              setReplyLoading(true);setReplyMsg('');
                              try{
                                const ticketRef=notification.title?.match(/ES-[A-Z0-9]+|ST-[A-Z0-9]+/)?.[0]||'RE-'+Date.now().toString(36).toUpperCase();
                                const r=await fetch('/api/send-support-email',{
                                  method:'POST',
                                  headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('auth_token')},
                                  body:JSON.stringify({
                                    ticketId:ticketRef,
                                    userEmail:user?.email,
                                    replyToEmail:user?.email,
                                    firstName:user?.username||'User',
                                    lastName:'',
                                    userName:user?.username||user?.email?.split('@')[0],
                                    userPlan:user?.plan||'Free',
                                    category:'Reply to Support',
                                    priority:'Normal',
                                    subject:'Re: '+(notification.title||'Support Reply'),
                                    message:replyText,
                                    device:navigator.userAgent.split(')')[0]+')'
                                  })
                                });
                                const d=await r.json();
                                if(r.ok){setReplyMsg('✅ Reply sent successfully!');setReplyText('');setTimeout(()=>{setShowReplyForm(null);setReplyMsg('');},2500);}
                                else setReplyMsg('❌ '+(d.error||'Failed to send'));
                              }catch(e){setReplyMsg('❌ Network error');}
                              setReplyLoading(false);
                            }}
                            disabled={replyLoading||!replyText.trim()}
                            style={{width:'100%',padding:'12px',borderRadius:'8px',border:'none',background:replyText.trim()&&!replyLoading?'linear-gradient(135deg,#8b5cf6,#7c3aed)':'rgba(255,255,255,0.1)',color:replyText.trim()&&!replyLoading?'white':'#64748b',fontWeight:'700',cursor:replyText.trim()&&!replyLoading?'pointer':'default',fontSize:'14px',transition:'all 0.3s'}}
                          >{replyLoading?'Sending...':'Send Reply'}</button>
                          {replyMsg&&<p style={{color:replyMsg.startsWith('✅')?'#34d399':'#f87171',fontSize:'13px',marginTop:'10px',textAlign:'center',fontWeight:'500'}}>{replyMsg}</p>}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p style={{
                      color: '#cbd5e1',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      margin: '0 0 16px 0'
                    }}>
                      {notification.message && notification.message.length > 150 ? notification.message.substring(0, 150) + '...' : notification.message}
                    </p>
                  )}
                  
                  {notification.actions && notification.actions.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {notification.actions.map((action, index) => (
                        <button
                          key={index}
                          onClick={() => handleAction(action.action, notification.id)}
                          style={{
                            padding: '8px 16px',
                            background: 'rgba(99, 102, 241, 0.2)',
                            border: '1px solid rgba(99, 102, 241, 0.4)',
                            borderRadius: '8px',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = 'rgba(99, 102, 241, 0.3)';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = 'rgba(99, 102, 241, 0.2)';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        
        {filteredNotifications.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#94a3b8'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <h3 style={{ color: '#cbd5e1', marginBottom: '8px' }}>No notifications found</h3>
            <p>You're all caught up! New notifications will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;